/**
 * SHER-799 - the client half of the invitation key-handoff.
 *
 * The server can verify that someone holds a valid invitation without ever being able to unwrap
 * the investigation key that invitation carries. That property lives HERE, because it is a
 * property of what never leaves the browser:
 *
 *   S           128-bit CSPRNG root token. Minted by the inviter, carried to the invitee ONLY in
 *               the invite URL's fragment (which browsers do not send to the server), and never
 *               transmitted to the API.
 *   wrapKey     HKDF(S, wrapSalt, "sher-invitation-wrap-v1"). Wraps/unwraps the investigation
 *               key. Client-only, forever.
 *   authProof   HKDF(S, wrapSalt, "sher-invitation-auth-v1"). Sent to the API at claim time; the
 *               API stores only SHA-256(authProof).
 *
 * The two derivations share a root but are domain-separated by their `info` label, so possession
 * of authProof yields nothing about wrapKey. That is the whole trick: the server gets a credential
 * it can check and a blob it cannot open.
 *
 * WHY A REAL KDF AND AEAD, WHEN THE REST OF THE REPO USES CryptoJS PASSPHRASE MODE. This is a new
 * path, so it starts correct rather than inheriting the legacy scheme:
 *
 *   - HKDF-SHA256 rather than PBKDF2. S is already 128 bits of CSPRNG output, so there is nothing
 *     to stretch; what is needed is clean domain separation, which is exactly what HKDF's `info`
 *     parameter is for. A slow password-KDF here would buy nothing and cost the invitee seconds.
 *   - AES-GCM rather than AES-CBC. GCM is authenticated: unwrapping with the wrong key THROWS.
 *     The legacy path returns "" instead, and that silent empty string is the root of the
 *     SHER-344 / SHER-632 / SHER-663 family - an empty key that gets persisted and quietly
 *     encrypts a tenant's patient data under nothing. On this path that failure is now loud.
 *
 * The general migration of the repo's existing encryptData/decryptData off CryptoJS is SHER-249
 * and is NOT this file's business.
 */

/** Domain-separation labels. Changing either one invalidates every outstanding invitation, which
 *  is why they carry an explicit version suffix rather than being edited in place. */
const WRAP_INFO = "sher-invitation-wrap-v1";
const AUTH_INFO = "sher-invitation-auth-v1";

/** Marks the wrap format inside the stored blob, so a future scheme is distinguishable from this
 *  one by looking at the value rather than by guessing from its length. */
const WRAP_FORMAT_PREFIX = "siw1:";

const ROOT_TOKEN_BYTES = 16;   // 128 bits, per the SHER-769 decision doc
const WRAP_SALT_BYTES = 32;
const AUTH_PROOF_BITS = 256;
const GCM_IV_BYTES = 12;

/**
 * SubtleCrypto, resolved at call time rather than at module load.
 *
 * Not a test accommodation: browsers expose `crypto.subtle` only in a secure context, so on a
 * plain http:// origin it is `undefined` and every call below would fail as
 * "Cannot read properties of undefined". An invitee hitting that would see a generic crash on the
 * one screen where a crash is indistinguishable from "your invitation is broken". Fail with a
 * sentence that names the cause instead.
 */
function subtle(): SubtleCrypto {
    const webcrypto = (globalThis as { crypto?: Crypto }).crypto;
    if (!webcrypto || !webcrypto.subtle) {
        throw new Error(
            "SHER-799: WebCrypto (crypto.subtle) is unavailable. Browsers expose it only in a " +
            "secure context, so this page must be served over HTTPS (or from localhost). The " +
            "invitation key-handoff cannot run without it.",
        );
    }
    return webcrypto.subtle;
}

function randomBytes(length: number): Uint8Array {
    const bytes = new Uint8Array(length);
    (globalThis as { crypto?: Crypto }).crypto!.getRandomValues(bytes);
    return bytes;
}

function toBase64(bytes: Uint8Array): string {
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

function fromBase64(value: string): Uint8Array {
    const binary = atob(value);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}

/** The root token S. Base64 rather than hex only to keep the invite URL shorter. */
export function generateRootToken(): string {
    return toBase64(randomBytes(ROOT_TOKEN_BYTES));
}

/** A fresh per-invitation salt. One per invitation, never reused: reuse would make the wrap key
 *  precomputable across every invitation that shared it. */
export function mintWrapSalt(): string {
    return toBase64(randomBytes(WRAP_SALT_BYTES));
}

export interface InvitationSecrets {
    /** Never leaves this module's caller. Non-extractable, so it cannot be read back out. */
    wrapKey: CryptoKey;
    /** Base64. The only derivation that is sent to the API. */
    authProof: string;
}

/**
 * Derive both secrets from one root token.
 *
 * `wrapKey` is imported as NON-EXTRACTABLE: `exportKey` on it throws. That is deliberate - it means
 * no future caller can accidentally serialise the wrap key into a request body, a redux store, a
 * Slack error report or localStorage. The key can be used and then forgotten, which is the only
 * lifecycle that keeps the zero-knowledge claim true.
 */
export async function deriveInvitationSecrets(rootToken: string, wrapSalt: string): Promise<InvitationSecrets> {
    const salt = fromBase64(wrapSalt);
    const encoder = new TextEncoder();

    const rootKey = await subtle().importKey(
        "raw",
        fromBase64(rootToken) as unknown as BufferSource,
        "HKDF",
        false,
        ["deriveBits", "deriveKey"],
    );

    const wrapKey = await subtle().deriveKey(
        { name: "HKDF", hash: "SHA-256", salt: salt as unknown as BufferSource, info: encoder.encode(WRAP_INFO) as unknown as BufferSource },
        rootKey,
        { name: "AES-GCM", length: 256 },
        false,
        ["encrypt", "decrypt"],
    );

    const authProofBits = await subtle().deriveBits(
        { name: "HKDF", hash: "SHA-256", salt: salt as unknown as BufferSource, info: encoder.encode(AUTH_INFO) as unknown as BufferSource },
        rootKey,
        AUTH_PROOF_BITS,
    );

    return { wrapKey, authProof: toBase64(new Uint8Array(authProofBits)) };
}

/**
 * Wrap the investigation key under the invitation's wrap key.
 *
 * A fresh IV per call, prefixed to the ciphertext. Reusing an IV under one AES-GCM key is
 * catastrophic rather than merely weak, and the only way to guarantee it does not happen is to
 * generate it here instead of accepting one from a caller.
 */
export async function wrapInvestigationKey(wrapKey: CryptoKey, investigationKey: string): Promise<string> {
    const iv = randomBytes(GCM_IV_BYTES);
    const ciphertext = await subtle().encrypt(
        { name: "AES-GCM", iv: iv as unknown as BufferSource },
        wrapKey,
        new TextEncoder().encode(investigationKey) as unknown as BufferSource,
    );

    const payload = new Uint8Array(iv.length + ciphertext.byteLength);
    payload.set(iv, 0);
    payload.set(new Uint8Array(ciphertext), iv.length);
    return WRAP_FORMAT_PREFIX + toBase64(payload);
}

/**
 * Unwrap, or THROW.
 *
 * There is no "" return and no null return on this path, by design. Every silent-empty incident in
 * this repo's encryption history began with an unwrap that answered "" to a wrong key and let the
 * caller persist the result. A wrong key, a corrupted blob or a tampered ciphertext all land in
 * the same place here: an exception the caller cannot mistake for a value.
 */
export async function unwrapInvestigationKey(wrapKey: CryptoKey, wrapped: string): Promise<string> {
    if (!wrapped || !wrapped.startsWith(WRAP_FORMAT_PREFIX)) {
        throw new Error(
            `SHER-799: not an invitation-wrapped key (expected the "${WRAP_FORMAT_PREFIX}" prefix). ` +
            `A legacy constant-passphrase wrap must go through the legacy path, not this one.`,
        );
    }

    const payload = fromBase64(wrapped.slice(WRAP_FORMAT_PREFIX.length));
    if (payload.length <= GCM_IV_BYTES) {
        throw new Error("SHER-799: invitation-wrapped key is truncated - no ciphertext after the IV.");
    }

    const iv = payload.slice(0, GCM_IV_BYTES);
    const ciphertext = payload.slice(GCM_IV_BYTES);

    let plaintext: ArrayBuffer;
    try {
        plaintext = await subtle().decrypt(
            { name: "AES-GCM", iv: iv as unknown as BufferSource },
            wrapKey,
            ciphertext as unknown as BufferSource,
        );
    } catch (cause) {
        // WebCrypto reports every AEAD failure as a bare OperationError with no message. Replace it
        // with one that says what actually happened, because the caller's only other option is to
        // guess - and the historical guess was "the key must be empty".
        throw new Error(
            "SHER-799: invitation unwrap failed authentication. The invite link does not match this " +
            "invitation, or the wrapped key has been altered. The investigation key was NOT recovered " +
            "and nothing should be saved.",
        );
    }

    return new TextDecoder().decode(plaintext);
}

/** Exported for the tests and for any caller that needs to recognise the format. */
export const INVITATION_WRAP_FORMAT_PREFIX = WRAP_FORMAT_PREFIX;
